// src/components/chat/OpenChatWidget.jsx
import React, { useState } from 'react';
import { X, Minimize2, Maximize2, MessageCircle } from 'lucide-react';

const OpenChatWidget = ({ 
  userId, 
  isOpen, 
  onClose, 
  config = {} 
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const defaultConfig = {
    type: 'direct',
    theme: 'light',
    color: '#3B82F6',
    height: '500px',
    width: '100%',
    showHeader: true,
    showBranding: true,
    greeting: 'Hi! How can I help you today?',
    placeholder: 'Type your message...',
    ...config
  };

  const openChatUrl = React.useMemo(() => {
    const baseUrl = 'https://openchat.co.za/chat';
    const params = new URLSearchParams({
      type: defaultConfig.type,
      userId: userId,
      theme: defaultConfig.theme,
      color: defaultConfig.color,
      height: defaultConfig.height,
      width: defaultConfig.width,
      showHeader: defaultConfig.showHeader,
      showBranding: defaultConfig.showBranding,
      greeting: defaultConfig.greeting,
      placeholder: defaultConfig.placeholder
    });
    
    return `${baseUrl}?${params.toString()}`;
  }, [userId, defaultConfig]);

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-4 right-4 md:right-4 left-4 md:left-auto z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div 
        className={`bg-white rounded-lg shadow-2xl border border-gray-200 transition-all duration-300 ${
          isMinimized 
            ? 'w-full md:w-80 h-16' 
            : 'w-full md:w-[420px] h-screen md:h-[550px] max-h-[600px]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 bg-[#5548e8] text-white rounded-t-lg">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            <span className="font-medium text-sm">Let's Chat</span>
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-blue-700 rounded transition-colors"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
            
            <button
              onClick={onClose}
              className="p-1 hover:bg-blue-700 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        {!isMinimized && (
          <div className="relative h-[calc(100%-3rem)]">
            {!isLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                <div className="flex items-center gap-2 text-gray-600">
                  <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                  <span className="text-sm">Loading chat...</span>
                </div>
              </div>
            )}
            
            <iframe
              src={openChatUrl}
              className="w-full h-full border-none rounded-b-lg"
              onLoad={() => setIsLoaded(true)}
              title="OpenChat"
              allow="microphone; camera"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenChatWidget;